import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import CustomHeader from "../../components/Auth/CustomHeader";

const WorkerEngagementAgreement = () => {
  document.title = "Worker Engagement Agreement | JobArk";

  return (
    <>
      <CustomHeader />
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={9} xl={8}>
              <Card className="overflow-hidden">
                <CardHeader className="bg-primary">
                  <h4 className="text-white mb-0 text-center py-2">
                    JobArk Worker Engagement Agreement
                  </h4>
                </CardHeader>
                <CardBody className="p-4 p-md-5 worker-engagement-agreement">
                  <p className="text-muted small mb-4">Version 1.0 — 29/07/26</p>

                  <section className="mb-4">
                    <h5 className="mb-3">1. Introduction</h5>
                    <p>
                      This Worker Engagement Agreement (&quot;Agreement&quot;)
                      sets out the terms under which an individual
                      (&quot;Worker&quot;) may be engaged by JobArk Limited
                      (&quot;JobArk&quot;).
                    </p>
                    <p className="mb-0">
                      By completing onboarding, accepting this Agreement and
                      becoming an approved Worker, the Worker agrees to be bound
                      by the terms set out below.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">2. Definitions</h5>
                    <p>For the purposes of this Agreement:</p>
                    <ul>
                      <li>
                        <strong>&quot;JobArk&quot;</strong> means Job Ark Ltd, an
                        employment business supplying temporary agency workers to
                        clients.
                      </li>
                      <li>
                        <strong>&quot;Worker&quot;</strong> means the individual
                        who registers with JobArk and undertakes assignments as a
                        worker and, where applicable, an agency worker under the
                        Agency Workers Regulations 2010.
                      </li>
                      <li>
                        <strong>&quot;Assignment&quot;</strong> means a period
                        during which the Worker undertakes work for a client
                        through JobArk.
                      </li>
                      <li>
                        <strong>&quot;Client&quot;</strong> or{" "}
                        <strong>&quot;Hirer&quot;</strong> means any organisation
                        to which JobArk supplies the Worker to perform work.
                      </li>
                      <li>
                        <strong>&quot;Key Information Document&quot;</strong>{" "}
                        (KID) means the document provided to the Worker before
                        work-finding services are supplied, containing prescribed
                        information under the Conduct Regulations.
                      </li>
                      <li>
                        <strong>&quot;Agreement&quot;</strong> means this Worker
                        Engagement Agreement, as updated from time to time.
                      </li>
                      <li>
                        <strong>&quot;Confidential Information&quot;</strong>{" "}
                        means any non-public information relating to JobArk,
                        clients, assignments, commercial operations or other
                        sensitive matters.
                      </li>
                      <li>
                        <strong>&quot;Data Protection Legislation&quot;</strong>{" "}
                        means the UK GDPR, the Data Protection Act 2018 and any
                        other applicable data protection laws.
                      </li>
                      <li>
                        <strong>&quot;Working Time Regulations&quot;</strong>{" "}
                        means the Working Time Regulations 1998.
                      </li>
                      <li>
                        <strong>&quot;Agency Workers Regulations&quot;</strong>{" "}
                        means the Agency Workers Regulations 2010.
                      </li>
                      <li>
                        <strong>&quot;Right to Work Evidence&quot;</strong> means
                        documentation or share codes demonstrating the
                        Worker&apos;s legal right to work in the UK.
                      </li>
                      <li>
                        <strong>&quot;Special Category Data&quot;</strong> means
                        personal data requiring additional protection under UK
                        GDPR, including health information, disability-related
                        information and criminal records data.
                      </li>
                    </ul>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">3. Purpose of Agreement</h5>
                    <p>
                      JobArk operates as a workforce co-ordination business that
                      identifies, engages and supplies suitable workers to
                      support client workforce requirements.
                    </p>
                    <p className="mb-0">
                      This Agreement governs the ongoing relationship between
                      JobArk and the Worker and applies to all opportunities
                      offered through JobArk unless otherwise agreed in writing.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">4. Nature of Engagement</h5>
                    <p>The Worker acknowledges and agrees that:</p>
                    <ul className="mb-0">
                      <li>Approval as a JobArk Worker does not guarantee work.</li>
                      <li>
                        JobArk is under no obligation to offer opportunities.
                      </li>
                      <li>
                        The Worker is under no obligation to accept opportunities.
                      </li>
                      <li>
                        Opportunities depend on client demand, operational
                        requirements and worker suitability.
                      </li>
                    </ul>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">5. Employment Status</h5>
                    <p>
                      The parties intend that, when the Worker undertakes an
                      assignment through JobArk, the Worker will be engaged as a
                      worker for the purposes of applicable employment legislation
                      and, where relevant, as an agency worker for the purposes of
                      the Agency Workers Regulations 2010.
                    </p>
                    <p>
                      Nothing in this Agreement is intended to remove, reduce or
                      exclude any statutory right to which the Worker is entitled.
                    </p>
                    <p>
                      Unless expressly agreed in writing, this Agreement does not
                      create a contract of employment between JobArk and the
                      Worker and does not guarantee continuous employment,
                      continuous engagement, minimum hours or minimum earnings.
                    </p>
                    <p className="mb-0">
                      Each assignment is separate and does not create any
                      entitlement to future assignments.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">6. Registration and Approval</h5>
                    <p>
                      To become eligible for opportunities through JobArk,
                      Workers must:
                    </p>
                    <ul>
                      <li>Complete registration and onboarding.</li>
                      <li>
                        Provide accurate information and required documentation.
                      </li>
                      <li>Complete required verification checks.</li>
                      <li>Accept this Agreement.</li>
                    </ul>
                    <p className="mb-0">
                      JobArk may approve, reject, suspend or remove any
                      application at its discretion.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">7. Right to Work</h5>
                    <p>
                      The Worker confirms that they have the legal right to work
                      in the United Kingdom and agrees to:
                    </p>
                    <ul>
                      <li>Provide genuine right-to-work documentation.</li>
                      <li>
                        Notify JobArk of any changes affecting their right to
                        work.
                      </li>
                      <li>Cooperate with compliance or verification checks.</li>
                    </ul>
                    <p>
                      JobArk may suspend or withdraw access to assignments if the
                      Worker does not provide satisfactory right to work evidence,
                      if right to work evidence expires, or if JobArk is unable to
                      complete required verification checks. The Worker must
                      provide updated right to work evidence before any relevant
                      expiry date.
                    </p>
                    <p className="mb-0">
                      Providing false or misleading information may result in
                      immediate removal from the JobArk workforce.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">8. Qualifications and Compliance</h5>
                    <p>The Worker is responsible for ensuring that:</p>
                    <ul>
                      <li>
                        Qualifications, licences and certifications are genuine,
                        valid and current.
                      </li>
                      <li>
                        Uploaded documentation is accurate and belongs to the
                        Worker.
                      </li>
                      <li>
                        JobArk is notified immediately of any changes.
                      </li>
                    </ul>
                    <p className="mb-0">
                      JobArk may request evidence or conduct verification checks
                      at any time.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">9. Key Terms and Assignment Details</h5>
                    <p>
                      JobArk will provide the Worker with written details of each
                      assignment before the assignment starts, where reasonably
                      practicable. Those details may include:
                    </p>
                    <ul>
                      <li>the identity of the client or hirer;</li>
                      <li>
                        the expected start date and, where known, end date;
                      </li>
                      <li>the location of the assignment;</li>
                      <li>the type of work and duties required;</li>
                      <li>the expected hours or shift pattern;</li>
                      <li>the applicable hourly or daily rate of pay;</li>
                      <li>the payment interval;</li>
                      <li>any authorised expenses arrangements;</li>
                      <li>any health and safety requirements;</li>
                      <li>
                        any required qualifications, licences, checks or training;
                        and
                      </li>
                      <li>any applicable notice or cancellation arrangements.</li>
                    </ul>
                    <p>
                      The Worker is not required to accept any assignment offered
                      by JobArk. Once the Worker accepts an assignment, the Worker
                      must comply with the agreed assignment details unless
                      prevented by illness, emergency or another reasonable cause.
                    </p>
                    <h6 className="mt-3 mb-2">No Fees to Worker</h6>
                    <p className="mb-0">
                      JobArk will not charge the Worker a fee for providing
                      work-finding services, except where permitted by law and
                      expressly agreed in writing.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">10. Opportunities and Placements</h5>
                    <p>
                      JobArk may contact Workers regarding opportunities that
                      match their skills, qualifications, experience, availability
                      and client requirements.
                    </p>
                    <p className="mb-0">
                      Selection remains at JobArk&apos;s discretion. Acceptance of
                      one opportunity does not create entitlement to future
                      opportunities.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">11. Worker Responsibilities</h5>
                    <p>The Worker agrees to:</p>
                    <ul className="mb-0">
                      <li>Act professionally at all times.</li>
                      <li>Perform duties with reasonable care and skill.</li>
                      <li>Follow lawful and reasonable instructions.</li>
                      <li>Maintain professional conduct.</li>
                      <li>
                        Communicate promptly regarding availability or issues.
                      </li>
                      <li>Maintain accurate contact details.</li>
                    </ul>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">12. Health and Safety</h5>
                    <p>
                      JobArk will take reasonable steps to obtain relevant health
                      and safety information from clients and communicate relevant
                      assignment requirements to the Worker. The Worker must
                      comply with all lawful and reasonable health and safety
                      instructions, attend required inductions or training, use
                      required PPE, and report accidents, incidents, near misses
                      and hazards promptly to both JobArk and the client.
                    </p>
                    <p className="mb-0">
                      Failure to comply may result in removal from assignments and
                      suspension.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">
                      13. Attendance, Cancellations and No-Shows
                    </h5>
                    <p>
                      Where a Worker has accepted an opportunity, the Worker
                      agrees to attend the assignment at the agreed time and
                      location and to perform the assignment as agreed.
                    </p>
                    <p>
                      If the Worker is unable to attend, the Worker must notify
                      JobArk as soon as reasonably possible and provide the reason
                      for their non-attendance.
                    </p>
                    <p>
                      Subject to any statutory entitlement or other legal
                      requirement, the Worker will not be entitled to payment for
                      any assignment, or part of an assignment, which the Worker
                      does not attend or perform.
                    </p>
                    <p>
                      JobArk will consider any explanation provided by the Worker,
                      including illness, emergency, disability-related reasons,
                      pregnancy-related reasons or any other protected or
                      exceptional circumstances, before deciding whether to
                      restrict the Worker&apos;s access to future assignments.
                      Repeated late cancellations, non-attendance, no-shows or
                      unauthorised absences may result in suspension or removal.
                    </p>
                    <p>
                      Any such decision will be made reasonably and in accordance
                      with applicable law.
                    </p>
                    <p className="mb-0">
                      Nothing in this Agreement is intended to remove, reduce or
                      exclude any statutory right to which the Worker is entitled.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">14. Timesheets and Records of Work</h5>
                    <p>
                      The Worker must submit accurate timesheets or other records
                      of hours worked in the manner and by the deadline notified
                      by JobArk. JobArk does not reimburse expenses.
                    </p>
                    <p className="mb-0">
                      If there is a discrepancy or dispute regarding hours worked,
                      JobArk may make reasonable enquiries with the Worker and the
                      client. JobArk will not unreasonably delay payment of sums
                      properly due to the Worker.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">15. Payment</h5>
                    <p>
                      JobArk will pay the Worker for hours actually worked and
                      properly evidenced, subject to applicable tax, National
                      Insurance, pension contributions and any other lawful
                      deductions.
                    </p>
                    <p>
                      Payment will be made at the rate and interval notified to
                      the Worker in the applicable assignment details or other
                      written payment information.
                    </p>
                    <p>
                      JobArk will not withhold payment solely because JobArk has
                      not been paid by the client.
                    </p>
                    <p className="mb-0">
                      Where JobArk reasonably believes that an overpayment has
                      been made, JobArk may recover the overpayment in accordance
                      with applicable law, after notifying the Worker of the basis
                      for the proposed recovery.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">16. Working Time and Holiday Pay</h5>
                    <p>
                      The Worker is entitled to paid annual leave in accordance
                      with the Working Time Regulations 1998. Holiday entitlement
                      and holiday pay will be calculated in accordance with
                      applicable law and JobArk&apos;s holiday arrangements as
                      notified to the Worker from time to time.
                    </p>
                    <p>
                      The Worker must request holiday in accordance with
                      JobArk&apos;s notified procedures. Approval of holiday
                      requests may be subject to operational requirements and
                      assignment commitments.
                    </p>
                    <p>
                      The Worker is responsible for notifying JobArk if the Worker
                      believes that their working hours, including work undertaken
                      for any other person, may exceed applicable working time
                      limits. The Worker is entitled to rest breaks, daily rest
                      and weekly rest in accordance with applicable law.
                    </p>
                    <p className="mb-0">
                      Where permitted by law, JobArk may pay rolled-up holiday pay
                      to eligible Workers. Where rolled-up holiday pay is used, it
                      will be calculated in accordance with applicable law, paid
                      as an additional and separately identified element of pay,
                      and shown separately on the Worker&apos;s payslip.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">17. Agency Workers Regulations</h5>
                    <p>
                      Where the Agency Workers Regulations 2010 apply, JobArk will
                      comply with its obligations under those Regulations.
                    </p>
                    <p>
                      The Worker must provide accurate information requested by
                      JobArk to assess rights under the Agency Workers Regulations
                      2010, including details of any previous assignments
                      undertaken with the same client or any connected hirer.
                    </p>
                    <p className="mb-0">
                      The Worker must promptly notify JobArk if they believe that
                      they have not received any entitlement due under the Agency
                      Workers Regulations 2010.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">18. Confidentiality</h5>
                    <p>
                      The Worker must keep confidential any non-public information
                      relating to JobArk, clients, assignments or commercial
                      operations.
                    </p>
                    <p>Nothing in this Agreement prevents the Worker from:</p>
                    <ul>
                      <li>
                        making a protected disclosure under whistleblowing
                        legislation;
                      </li>
                      <li>
                        reporting a criminal offence or regulatory breach to an
                        appropriate authority;
                      </li>
                      <li>
                        cooperating with a regulator, court, tribunal or law
                        enforcement body;
                      </li>
                      <li>
                        taking legal proceedings or obtaining professional advice;
                      </li>
                      <li>
                        discussing pay or working conditions where such discussion
                        is protected by law; or
                      </li>
                      <li>exercising any statutory right.</li>
                    </ul>
                    <p className="mb-0">
                      Confidentiality obligations continue after engagement ends.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">19. Client Engagements</h5>
                    <p>
                      Nothing in this Agreement prevents the Worker from accepting
                      employment or engagement directly with any client or hirer,
                      subject to the Worker complying with their confidentiality
                      obligations and any assignment commitments already accepted
                      through JobArk.
                    </p>
                    <p className="mb-0">
                      The Worker must not dishonestly misrepresent their
                      availability, submit false timesheets, misuse confidential
                      information, or divert to another arrangement any shift or
                      assignment that the Worker has already accepted through
                      JobArk without notifying JobArk as soon as reasonably
                      practicable.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">
                      20. Equality, Dignity and Professional Conduct
                    </h5>
                    <p>
                      The Worker must not engage in unlawful discrimination,
                      harassment, victimisation, bullying or other inappropriate
                      conduct during any assignment or in communications with
                      JobArk, clients, colleagues, service users or members of the
                      public.
                    </p>
                    <p className="mb-0">
                      The Worker must promptly report to JobArk any concerns
                      relating to discrimination, harassment, bullying,
                      safeguarding, unsafe working conditions or unlawful
                      treatment during an assignment.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">21. Data Protection</h5>
                    <p>
                      JobArk will process the Worker&apos;s personal data in
                      accordance with applicable data protection legislation,
                      including the UK GDPR and the Data Protection Act 2018, and
                      in accordance with JobArk&apos;s Worker Privacy Notice as
                      updated from time to time.
                    </p>
                    <p>
                      JobArk may process and share the Worker&apos;s personal data
                      where necessary for recruitment, onboarding, right to work
                      checks, compliance checks, assignment management, payroll,
                      tax, pension administration, client fulfilment, safeguarding,
                      health and safety, legal obligations, dispute management and
                      regulatory compliance.
                    </p>
                    <p>
                      Where necessary and lawful, JobArk may process special
                      category data and criminal records information, including
                      health information, disability-related information, right to
                      work documentation, background check information and DBS or
                      similar check information.
                    </p>
                    <p className="mb-0">
                      The Worker must ensure that information provided to JobArk
                      is accurate and must promptly notify JobArk of any relevant
                      changes.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">22. Suspension and Termination</h5>
                    <p>
                      JobArk may suspend access to assignments while it
                      investigates any concern relating to compliance, conduct,
                      safeguarding, health and safety, right to work, suspected
                      fraud, client complaint, or breach of this Agreement.
                    </p>
                    <p>
                      JobArk may remove the Worker from an assignment or terminate
                      the Worker&apos;s access to future assignments immediately
                      where JobArk reasonably considers that:
                    </p>
                    <ul>
                      <li>
                        the Worker has provided false or misleading information;
                      </li>
                      <li>
                        the Worker no longer satisfies right to work,
                        qualification, licence or compliance requirements;
                      </li>
                      <li>
                        the Worker has committed misconduct or serious misconduct;
                      </li>
                      <li>
                        the Worker has breached health and safety requirements;
                      </li>
                      <li>
                        the Worker has submitted false timesheets or expenses;
                      </li>
                      <li>
                        the Worker has breached confidentiality obligations;
                      </li>
                      <li>
                        continued engagement may create legal, regulatory,
                        safeguarding, operational or reputational risk; or
                      </li>
                      <li>
                        the Worker has materially breached this Agreement.
                      </li>
                    </ul>
                    <p>
                      Termination or suspension will not affect the Worker&apos;s
                      right to receive payment for sums properly due for work
                      performed before termination, subject to lawful deductions.
                    </p>
                    <p className="mb-0">
                      JobArk will not suspend, remove or terminate the Worker for
                      any reason prohibited by law, including unlawful
                      discrimination, whistleblowing, trade union activity or the
                      assertion of statutory rights.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">23. Changes To This Agreement</h5>
                    <p>
                      JobArk may update this Agreement from time to time. JobArk
                      will notify the Worker of material changes and may require
                      the Worker to confirm acceptance electronically before
                      accepting further assignments.
                    </p>
                    <p className="mb-0">
                      No change will affect rights or obligations that have
                      already accrued before the date the change takes effect,
                      unless expressly agreed or required by law.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h5 className="mb-3">24. Governing Law and Jurisdiction</h5>
                    <p>
                      This Agreement and any non-contractual obligations arising
                      out of or in connection with it are governed by the law of
                      England and Wales.
                    </p>
                    <p className="mb-0">
                      Subject to any statutory right to bring proceedings before
                      an employment tribunal or other competent tribunal or
                      authority, the courts of England and Wales shall have
                      jurisdiction in relation to disputes arising out of or in
                      connection with this Agreement.
                    </p>
                  </section>

                  <section className="mb-0">
                    <h5 className="mb-3">25. Acceptance</h5>
                    <p>
                      By accepting this Agreement electronically, the Worker
                      confirms that they have:
                    </p>
                    <ul className="mb-0">
                      <li>Read and understood this Agreement</li>
                      <li>Had the opportunity to ask questions</li>
                      <li>Agreed to be bound by its terms</li>
                    </ul>
                  </section>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default WorkerEngagementAgreement;
